import React, { useState, useEffect } from 'react';
import { Bell, Send, Trash2, Users, Crown, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiDelete, apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [target, setTarget] = useState('all');
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchAnnouncements(); }, []);

  const fetchAnnouncements = async () => {
    try {
      const data = await apiGet(API_ROUTES.admin.announcements);
      setAnnouncements(data.announcements || []);
    } catch (e) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const sendAnnouncement = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) return toast.error('Title and message required');
    setSending(true);
    try {
      const res = await apiFetch(API_ROUTES.admin.announcements, {
        method: 'POST',
        body: { title, message, target },
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Announcement sent!');
      setTitle(''); setMessage(''); setTarget('all'); setShowForm(false);
      fetchAnnouncements();
    } catch (e) {
      toast.error('Failed to send announcement');
    } finally {
      setSending(false);
    }
  };

  const deleteAnnouncement = async (id) => {
    try {
      await apiDelete(`${API_ROUTES.admin.announcements}/${id}`);
      toast.success('Deleted');
      fetchAnnouncements();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const targetIcon = (t) => {
    if (t === 'pro') return <Crown size={12} className="text-amber-400" />;
    if (t === 'free') return <Users size={12} className="text-blue-400" />;
    return <Globe size={12} className="text-green-400" />;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-100">Announcements</h1>
          <p className="text-sm text-gray-500 mt-1">Send notifications to users</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all text-sm font-medium"
        >
          <Bell size={14} />
          New Announcement
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <form onSubmit={sendAnnouncement} className="glass-card bg-dark-card p-5 mb-6 space-y-4">
          <input
            type="text"
            placeholder="Announcement title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-2.5 bg-dark-bg border border-dark-border rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-red-500/50"
          />
          <textarea
            placeholder="Message body..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="w-full px-4 py-2.5 bg-dark-bg border border-dark-border rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-red-500/50 resize-none"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">Target:</span>
              {['all', 'free', 'pro'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTarget(t)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    target === t ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-dark-bg text-gray-500 border border-dark-border hover:text-gray-300'
                  }`}
                >
                  {t === 'all' ? 'All Users' : t === 'pro' ? 'Pro Only' : 'Free Only'}
                </button>
              ))}
            </div>
            <button
              type="submit"
              disabled={sending}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all text-sm font-medium disabled:opacity-50"
            >
              <Send size={14} />
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      )}

      {/* Past announcements */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : announcements.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-dark-card border border-dark-border rounded-xl">
            No announcements yet
          </div>
        ) : (
          announcements.map((a) => (
            <div key={a.id} className="glass-card bg-dark-card p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-medium text-gray-200">{a.title}</h3>
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-dark-bg border border-dark-border">
                      {targetIcon(a.target)}
                      <span className="text-gray-500">{a.target}</span>
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">{a.message}</p>
                  <span className="text-[10px] text-gray-600">{a.created_at?.split('T')[0]}</span>
                </div>
                <button
                  onClick={() => deleteAnnouncement(a.id)}
                  className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-all"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
